import React, { useState, useEffect } from "react";
import { Grid, InputAdornment, TextField } from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import TaxCategorySelect from "./form/TaxCategorySelect";
import ReasonCodeSelect from "./form/ReasonCodeSelect";
import { useSelector } from "react-redux";

const TaxCategoryFields = ({
  control,
  errors,
  taxCategoryName,
  taxRateName,
  setValue,
  taxCategoryReadOnly = false,
}) => {
  const taxCategoryOptions =
    useSelector((state) => state.codeLists.taxCategoriesList) ?? [];
  const [disableTaxRate, setDisableTaxRate] = useState(false);
  const selectedTaxCategory = useWatch({ control, name: taxCategoryName });
  const isTaxCategoryE = selectedTaxCategory === "E";
  const isTaxCategoryZ = selectedTaxCategory === "Z";

  return (
    <>
      <Grid size={{ xs: 13, sm: 7, md: 6 }}>
        <TaxCategorySelect
          id={taxCategoryName}
          control={control}
          errors={errors}
          name={taxCategoryName}
          readOnly={taxCategoryReadOnly}
          onChange={(selected) => {
            if (!selected) {
              setValue(taxRateName, 0, { shouldValidate: true });
              setValue(taxCategoryName, null, { shouldValidate: true });
              return;
            }
            const taxObject =
              taxCategoryOptions.find(
                (t) => t.category === selected?.category,
              ) ?? null;
            const taxRate = taxObject?.rate ?? null;
            setDisableTaxRate(true);
            setValue(taxRateName, taxRate, { shouldValidate: true });
            setValue(taxCategoryName, selected?.category, {
              shouldValidate: true,
            });

            // Clear tax exemption reason if changing away from E
            if (selected?.category !== "E") {
              const reasonCodeFieldName = taxCategoryName.replace(
                ".id",
                ".tax_exemption_reason_code",
              );
              const reasonFieldName = taxCategoryName.replace(
                ".id",
                ".tax_exemption_reason",
              );
              setValue(reasonCodeFieldName, null, { shouldValidate: false });
              setValue(reasonFieldName, null, { shouldValidate: false });
            }
            if (selected?.category !== "Z") {
              const zeroRatedReasonCodeFieldName = taxCategoryName.replace(
                ".id",
                ".zero_rated_reason_code",
              );
              const zeroRatedReasonFieldName = taxCategoryName.replace(
                ".id",
                ".zero_rated_reason",
              );
              setValue(zeroRatedReasonCodeFieldName, null, {
                shouldValidate: false,
              });
              setValue(zeroRatedReasonFieldName, null, {
                shouldValidate: false,
              });
            }
          }}
        />
      </Grid>

      <Grid size={{ xs: 13, sm: 7, md: 6 }}>
        <Controller
          name={taxRateName}
          control={control}
          render={({ field, fieldState }) => {
            const handleChange = (e) => {
              const value = e.target.value;
              // Only allow numbers and decimals
              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                const numValue = value === "" ? 0 : parseFloat(value) || 0;
                field.onChange(Number(numValue.toFixed(2)));
              }
            };

            const formatValue = (val) => {
              if (val === "" || val === null || val === undefined) return "";
              return parseFloat(val).toFixed(2);
            };

            return (
              <TextField
                {...field}
                onChange={handleChange}
                data-testid="tax-rate-input"
                type="number"
                label="Tax Rate"
                fullWidth
                variant="outlined"
                slotProps={{
                  htmlInput: {
                    readOnly: disableTaxRate,
                    min: 0,
                    max: 100,
                    step: "0.01",
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    readOnly: disableTaxRate,
                  },
                }}
                value={formatValue(field.value)}
                InputProps={{
                  readOnly: disableTaxRate || taxCategoryReadOnly,
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            );
          }}
        />
      </Grid>

      {(isTaxCategoryE || isTaxCategoryZ) && (
        <Grid size={{ xs: 13, sm: 12, md: 12 }}>
          <ReasonCodeSelect
            name={taxCategoryName.replace(
              ".id",
              isTaxCategoryE
                ? ".tax_exemption_reason_code"
                : ".zero_rated_reason_code",
            )}
            label={
              isTaxCategoryE ? "Tax Exemption Reason" : "Zero Rated Reason"
            }
            control={control}
            errors={errors}
            readOnly={taxCategoryReadOnly}
            setValue={setValue}
            isTaxCategoryE={isTaxCategoryE}
          />
        </Grid>
      )}
    </>
  );
};

export default TaxCategoryFields;
